import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react'
import { isClient } from 'utils/runtimeUtils'
import {
  usePhotoBankStore,
  usePhotoBooksStore,
  useTilesStore,
} from '../RootStoreProvider'
import { CartStore } from './CartStore'
import { createCartItemAdapters } from './adapters/CartItemAdapter.utils'

const CartStoreContext = createContext<CartStore | null>(null)

const UNAVAILABLE_MESSAGE =
  'CartStore is unavailable. Cart is only constructed on the client.'

let clientCartStoreRef: CartStore | null = null

/** @public */
export function getCartStore(): CartStore {
  if (!clientCartStoreRef) {
    throw new Error(UNAVAILABLE_MESSAGE)
  }
  return clientCartStoreRef
}

/** @public */
export function useCartStore(): CartStore {
  const cartStore = useContext(CartStoreContext)
  if (!cartStore) {
    throw new Error(UNAVAILABLE_MESSAGE)
  }
  return cartStore
}

/** Returns null instead of throwing when cart is unavailable (SSR). */
export function useCartStoreOrNull(): CartStore | null {
  return useContext(CartStoreContext)
}

export function CartStoreProvider({ children }: PropsWithChildren) {
  const tilesStore = useTilesStore()
  const photoBooksStore = usePhotoBooksStore()
  const photoBankStore = usePhotoBankStore()

  const [cartStore] = useState<CartStore | null>(() => {
    if (!isClient()) return null

    const store = new CartStore(
      createCartItemAdapters({
        tilesStore,
        photoBooksStore,
        photoBankStore,
      })
    )
    clientCartStoreRef = store
    return store
  })

  useEffect(() => {
    if (!cartStore) return undefined
    return () => {
      cartStore.dispose()
      if (clientCartStoreRef === cartStore) {
        clientCartStoreRef = null
      }
    }
  }, [cartStore])

  return (
    <CartStoreContext.Provider value={cartStore}>
      {children}
    </CartStoreContext.Provider>
  )
}
